import { useEffect, useRef, useState } from 'react'

/**
 * Ay seçici — PnL Takip'in AYLIK görünümünde aralık takviminin yerini alır
 * (kullanıcı isteği 2026-08-01: aylık görünümde seçim birimi gün değil ay).
 *
 * Yerleşim DateRangePicker'ın kardeşi: aynı kutu/açılır pencere dili, aynı
 * Türkçe ay adları. Farkı, iki yanındaki ‹ › ile ay ay yürünebilmesi — en sık
 * yapılan iş "bir önceki ay" ve bunun için pencere açtırmak gereksiz.
 *
 * `maxAy` (dahil) sınırının ötesine geçilemez: ileri aylarda kayıt olamaz,
 * boş tabloya gitmenin anlamı yok. Sayfa bunu "içinde bulunulan ayın bir
 * sonrası" olarak veriyor — yarının kontratları öbür aya taşabiliyor.
 */

const AYLAR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
]
const KISA = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz',
              'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara']

/** YYYY-MM biçimi — karşılaştırmalar sözlük sırasıyla yapılabilsin diye. */
export function ayIso(yil: number, ayIdx: number): string {
  return `${yil}-${String(ayIdx + 1).padStart(2, '0')}`
}

export function MonthPicker({
  ay,
  maxAy,
  onChange,
}: {
  ay: string // YYYY-MM
  maxAy: string // YYYY-MM (dahil) — bunun ötesine geçilemez
  onChange: (ay: string) => void
}) {
  const [acik, setAcik] = useState(false)
  /** Açılır pencerede gezilen yıl — seçim yapılmadan yıl değiştirilebilsin. */
  const [yilGorunum, setYilGorunum] = useState(() => parseInt(ay.slice(0, 4), 10))
  const kok = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!acik) return
    const disari = (e: MouseEvent) => {
      if (kok.current && !kok.current.contains(e.target as Node)) setAcik(false)
    }
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAcik(false)
    }
    document.addEventListener('mousedown', disari)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', disari)
      document.removeEventListener('keydown', esc)
    }
  }, [acik])

  const yil = parseInt(ay.slice(0, 4), 10)
  const ayIdx = parseInt(ay.slice(5, 7), 10) - 1

  const kaydir = (adim: number) => {
    const d = new Date(yil, ayIdx + adim, 1)
    onChange(ayIso(d.getFullYear(), d.getMonth()))
  }
  const oncekiVar = true // geriye sınır yok — kayıt ne kadar eskiyse o kadar
  const sonrakiVar = ayIso(yil, ayIdx) < maxAy

  return (
    <div className="mp" ref={kok}>
      <button
        type="button"
        className="pager-btn"
        onClick={() => kaydir(-1)}
        disabled={!oncekiVar}
        aria-label="Önceki ay"
      >
        ‹
      </button>
      <button
        type="button"
        className="mp-btn"
        onClick={() => {
          setYilGorunum(yil)
          setAcik((v) => !v)
        }}
        aria-haspopup="dialog"
        aria-expanded={acik}
        title="Ay seç"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round"
             aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
        <span>{AYLAR[ayIdx]} {yil}</span>
      </button>
      <button
        type="button"
        className="pager-btn"
        onClick={() => kaydir(1)}
        disabled={!sonrakiVar}
        aria-label="Sonraki ay"
      >
        ›
      </button>

      {acik && (
        <div className="mp-pop" role="dialog" aria-label="Ay seç">
          <div className="drp-head">
            <button type="button" className="pager-btn"
                    onClick={() => setYilGorunum((y) => y - 1)}
                    aria-label="Önceki yıl">‹</button>
            <span className="drp-title">{yilGorunum}</span>
            <button type="button" className="pager-btn"
                    onClick={() => setYilGorunum((y) => y + 1)}
                    disabled={ayIso(yilGorunum, 0) > maxAy}
                    aria-label="Sonraki yıl">›</button>
          </div>
          <div className="mp-grid">
            {KISA.map((ad, i) => {
              const iso = ayIso(yilGorunum, i)
              return (
                <button
                  key={ad}
                  type="button"
                  className={'mp-ay' + (iso === ay ? ' mp-secili' : '')}
                  disabled={iso > maxAy}
                  onClick={() => {
                    onChange(iso)
                    setAcik(false)
                  }}
                >
                  {ad}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
